import HorikawaLogo from "../../image/horikawa_logo.png";
import TakaokaLogo from "../../image/takaoka_logo.png";
import TonamiLogo from "../../image/tonami_logo.png";
import IshikawaLogo from "../../image/ishikawa_logo.png";
import FukuiLogo from "../../image/fukui_logo.png";
import MutsukawaLogo from "../../image/mutsukawa_logo.png";
import KomatsuLogo from "../../image/komatsu_logo.png";

export type RegionId =
  | "horikawa"
  | "takaoka"
  | "tonami"
  | "ishikawa"
  | "fukui"
  | "mutsukawa"
  | "komatsu";

export const regionIds: RegionId[] = [
  "horikawa",
  "takaoka",
  "tonami",
  "ishikawa",
  "fukui",
  "mutsukawa",
  "komatsu",
];

export interface CourseItem {
  number: number;
  target: string;
  weektime: string;
  holitime: string;
  count1: string;
  price1: string;
  explanation: string;
}

// Firestoreに保存する「色なし」のコースグループ
export interface RawCourseGroup {
  label: string;
  items: CourseItem[];
}

// 画面表示用に色を適用したコースグループ
export interface CourseGroup extends RawCourseGroup {
  titleColor: string;
  boxBg: string;
  dotColor: string;
}

export interface NewsItem {
  icons: string[];
  event: string;
  date: string;
}

export interface VideoInfo {
  name: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
}

// Firestore の `branches/{regionId}` ドキュメントの形
export interface FirestoreBranchDoc {
  roomName: string;
  zip: string;
  address: string;
  hasParking: boolean;
  freeTrialUrl: string;
  membershipUrl: string;
  loginUrl: string;
  showCoursesFeesTaikan: boolean;
  showCoursesFees: boolean;
  weekendCourses: RawCourseGroup[];
  weekdayCourses: RawCourseGroup[];
}

// ロゴ・色・SEO用文言など、引き続きローカルで管理する支店情報
export interface LocalBranchInfo {
  id: RegionId;
  logo: string;
  weekendPalette: string[];
  weekdayPalette: string[];
  top: {
    metaTitle: string;
    metaDescription: string;
    regionKeywords: string[]; // 都道府県・市区町村・エリア名など
    ld: {
      name: string;
      description: string;
      postalCode?: string;
      streetAddress?: string;
      addressLocality: string;
      addressRegion: string;
      openingHours: string[];
      priceRange: string;
      video?: VideoInfo;
    };
    introFirstParagraph: string; // 「土日４つの種目で...」セクション冒頭の1文のみ支店ごとに異なる
    weekdaySentence?: string; // 平日セクション冒頭(showCoursesFeesTaikanがtrueの支店のみ使用)
    weekendSentence: string; // 土日セクション冒頭
    newsItems: NewsItem[];
  };
  coursesFeesDescription: string;
  coursesFeesTaikanDescription?: string;
}

// コンポーネントが実際に参照する、ローカル情報 + Firestoreデータ(色適用済み)の統合形
export interface BranchConfig extends LocalBranchInfo {
  roomName: string;
  zip: string;
  address: string;
  hasParking: boolean;
  freeTrialUrl: string;
  membershipUrl: string;
  loginUrl: string;
  showCoursesFeesTaikan: boolean;
  showCoursesFees: boolean;
  weekendCourses: CourseGroup[];
  weekdayCourses: CourseGroup[];
}

// Firestore側の入力(Console手入力や登録サイトのフォーム経由)では
// boolean/numberのつもりの値が文字列("true"/"1"等)で保存されることがある。
// JSでは空でない文字列は常にtruthyなので、"false"という文字列が
// 意図せずON扱いされる事故を防ぐため、読み込み時に型を正規化する。
const toBool = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.trim().toLowerCase() === "true";
  return Boolean(value);
};

const toNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
};

const normalizeCourseItem = (item: CourseItem, index: number): CourseItem => ({
  ...item,
  number: toNumber(item.number, index + 1),
});

const normalizeCourses = (courses: RawCourseGroup[]): RawCourseGroup[] =>
  courses.map((course) => ({
    ...course,
    items: course.items.map((item, index) => normalizeCourseItem(item, index)),
  }));

// Firestoreの生コース配列にローカルの色パレットを適用する
export const applyPalette = (
  courses: RawCourseGroup[],
  palette: string[],
): CourseGroup[] =>
  courses.map((course, index) => {
    const color = palette[index % palette.length] ?? "#333333";
    return {
      ...course,
      titleColor: color,
      boxBg: color,
      dotColor: "#ffabab",
    };
  });

// Firestoreドキュメント + ローカル情報を合成して画面用のBranchConfigを組み立てる
export const buildBranchConfig = (
  local: LocalBranchInfo,
  doc: FirestoreBranchDoc,
): BranchConfig => ({
  ...local,
  roomName: doc.roomName,
  zip: doc.zip,
  address: doc.address,
  hasParking: toBool(doc.hasParking),
  freeTrialUrl: doc.freeTrialUrl,
  membershipUrl: doc.membershipUrl,
  loginUrl: doc.loginUrl,
  showCoursesFeesTaikan: toBool(doc.showCoursesFeesTaikan),
  showCoursesFees: toBool(doc.showCoursesFees),
  weekendCourses: applyPalette(
    normalizeCourses(doc.weekendCourses ?? []),
    local.weekendPalette,
  ),
  weekdayCourses: applyPalette(
    normalizeCourses(doc.weekdayCourses ?? []),
    local.weekdayPalette,
  ),
});

const weekdayPalette5 = [
  "cornflowerblue",
  "#ff5e5e",
  "darkorange",
  "darkgreen",
  "blueviolet",
];

export const localBranchInfo: Record<RegionId, LocalBranchInfo> = {
  horikawa: {
    id: "horikawa",
    logo: HorikawaLogo,
    weekendPalette: ["#ff5e5e", "darkorange", "darkgreen", "blueviolet"],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle: "富山市堀川の土日だけ体操教室｜富山体操教室・バク転・体幹強化",
      metaDescription:
        "富山県富山市堀川の『土日だけ体操教室 堀川店』は、マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹、瞬発力を鍛える少人数制体操教室。バク転教室やパーソナル教室も併設し、体操式メニューで挑戦と成功をサポート。成長フィードバック付き無料体験レッスン受付中。",
      regionKeywords: ["富山県", "富山市", "堀川", "堀川店"],
      ld: {
        name: "土日だけ体操教室 堀川店",
        description:
          "富山市堀川の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒・トランポリンで運動能力を育てます。",
        addressLocality: "富山市",
        addressRegion: "富山県",
        openingHours: ["Sa 09:20-17:30", "Su 09:20-12:10"],
        priceRange: "7590円/月",
        video: {
          name: "土日だけ体操教室 堀川店 教室紹介動画",
          description:
            "富山市堀川の土日だけ体操教室の教室紹介動画です。マット運動・跳び箱・鉄棒・トランポリンで楽しく体操を学べます。",
          thumbnailUrl:
            "https://tht-group.co.jp/thumbnails/horikawa_thumbnail.jpg",
          contentUrl:
            "https://firebasestorage.googleapis.com/v0/b/thtgroup-95584.appspot.com/o/horikawa_pv.mp4?alt=media&token=6ba0193a-df45-4145-8342-bcc617a336bd",
        },
      },
      introFirstParagraph:
        "富山県富山市堀川に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒・トランポリンの４種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekdaySentence: "富山市堀川の 土日だけ体操教室〈体幹・体づくり教室〉 は、",
      weekendSentence: "富山市堀川の 土日だけ体操教室 は、少人数制だから",
      newsItems: [
        { icons: ["shaved_ice", "sunflower", "sun", "sun", "watermelon", "sunflower"], event: "夏休み", date: "2024年7月22日" },
        { icons: ["star", "", "", "tanabata", "star", ""], event: "七夕", date: "2024年7月7日" },
      ],
    },
    coursesFeesDescription:
      "富山市堀川の『土日だけ体操教室』土日開催の本格クラスと料金一覧。マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹を強化。キッズ・ジュニア初級からバク転・選手大会クラスまで少人数制で指導。無料体験レッスン受付中。",
    coursesFeesTaikanDescription:
      "富山市堀川の土日だけ体操教室〈体幹・体づくり教室〉は、体操式メニューで姿勢・バランス・柔軟性を強化。マット運動・跳び箱・鉄棒・トランポリンを用いた少人数制レッスンで成長フィードバック付き。無料体験受付中！",
  },

  takaoka: {
    id: "takaoka",
    logo: TakaokaLogo,
    weekendPalette: ["#7894f8", "#7894f8", "#ff5e5e", "darkorange", "darkgreen"],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle: "富山県高岡市の土日だけ体操教室｜高岡体操教室・バク転・体幹強化",
      metaDescription:
        "富山県高岡市の『土日だけ体操教室 高岡店』は、マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹、瞬発力を鍛える少人数制体操教室。バク転教室やパーソナル教室も併設し、体操式メニューで挑戦と成功をサポート。成長フィードバック付き無料体験レッスン受付中。",
      regionKeywords: ["富山県", "高岡市", "高岡店"],
      ld: {
        name: "土日だけ体操教室 高岡店",
        description:
          "富山県高岡市の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒・トランポリンで運動能力を育てます。",
        postalCode: "933-0042",
        streetAddress: "中川栄町６−１０",
        addressLocality: "高岡市",
        addressRegion: "富山県",
        openingHours: ["Sa 09:00-17:30", "Su 09:00-17:30"],
        priceRange: "7590円/月",
        video: {
          name: "土日だけ体操教室 高岡店 教室紹介動画",
          description:
            "富山県高岡市の土日だけ体操教室の教室紹介動画です。マット運動・跳び箱・鉄棒・トランポリンで楽しく体操を学べます。",
          thumbnailUrl:
            "https://tht-group.co.jp/thumbnails/takaoka_thumbnail.jpg",
          contentUrl:
            "https://firebasestorage.googleapis.com/v0/b/thtgroup-95584.appspot.com/o/takaoka_pv.mp4?alt=media&token=6ba0193a-df45-4145-8342-bcc617a336bd",
        },
      },
      introFirstParagraph:
        "富山県高岡市に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒・トランポリンの４種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekendSentence: "高岡市の 土日だけ体操教室 は、少人数制だから",
      newsItems: [],
    },
    coursesFeesDescription:
      "高岡市の『土日だけ体操教室』土日開催の本格クラスと料金一覧。マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹を強化。キッズ・ジュニア初級からバク転・選手大会クラスまで少人数制で指導。無料体験レッスン受付中。",
  },

  tonami: {
    id: "tonami",
    logo: TonamiLogo,
    weekendPalette: ["#7894f8", "#7894f8", "#ff5e5e", "darkorange", "darkgreen"],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle: "富山県砺波市の土日だけ体操教室｜砺波体操教室・バク転・体幹強化",
      metaDescription:
        "富山県砺波市の『土日だけ体操教室 砺波店』は、マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹、瞬発力を鍛える少人数制体操教室。バク転教室やパーソナル教室も併設し、体操式メニューで挑戦と成功をサポート。成長フィードバック付き無料体験レッスン受付中。",
      regionKeywords: ["富山県", "砺波市", "砺波店"],
      ld: {
        name: "土日だけ体操教室 砺波店",
        description:
          "富山県砺波市の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒・トランポリンで運動能力を育てます。",
        postalCode: "939-1328",
        streetAddress: "大辻７１４",
        addressLocality: "砺波市",
        addressRegion: "富山県",
        openingHours: ["Sa 09:00-17:30", "Su 09:00-17:30"],
        priceRange: "7590円/月",
        video: {
          name: "土日だけ体操教室 砺波店 教室紹介動画",
          description:
            "富山県砺波市の土日だけ体操教室の教室紹介動画です。マット運動・跳び箱・鉄棒・トランポリンで楽しく体操を学べます。",
          thumbnailUrl:
            "https://tht-group.co.jp/thumbnails/tonami_thumbnail.jpg",
          contentUrl:
            "https://firebasestorage.googleapis.com/v0/b/thtgroup-95584.appspot.com/o/tonami_pv.mp4?alt=media&token=6ba0193a-df45-4145-8342-bcc617a336bd",
        },
      },
      introFirstParagraph:
        "富山県砺波市に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒・トランポリンの４種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekendSentence: "砺波市の 土日だけ体操教室 は、少人数制だから",
      newsItems: [],
    },
    coursesFeesDescription:
      "砺波市の『土日だけ体操教室』土日開催の本格クラスと料金一覧。マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹を強化。キッズ・ジュニア初級からバク転・選手大会クラスまで少人数制で指導。無料体験レッスン受付中。",
  },

  ishikawa: {
    id: "ishikawa",
    logo: IshikawaLogo,
    weekendPalette: ["#ff5e5e", "darkorange", "darkgreen", "blueviolet"],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle:
        "石川県金沢市鳴和の土日だけ体操教室｜石川体操教室・バク転・体幹強化",
      metaDescription:
        "石川県金沢市鳴和の『土日だけ体操教室 鳴和店』は、マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹、瞬発力を鍛える少人数制体操教室。バク転教室やパーソナル教室も併設し、体操式メニューで挑戦と成功をサポート。成長フィードバック付き無料体験レッスン受付中。",
      regionKeywords: ["石川県", "金沢市", "鳴和"],
      ld: {
        name: "土日だけ体操教室 鳴和店",
        description:
          "石川県金沢市鳴和の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒・トランポリンで運動能力を育てます。",
        postalCode: "920-0804",
        streetAddress: "鳴和２丁目１０−４４",
        addressLocality: "金沢市",
        addressRegion: "石川県",
        openingHours: ["Sa 09:20-17:30", "Su 09:20-12:10"],
        priceRange: "7590円/月",
        video: {
          name: "土日だけ体操教室 鳴和店 教室紹介動画",
          description:
            "石川県金沢市鳴和の土日だけ体操教室の教室紹介動画です。マット運動・跳び箱・鉄棒・トランポリンで楽しく体操を学べます。",
          thumbnailUrl:
            "https://tht-group.co.jp/thumbnails/ishikawa_thumbnail.jpg",
          contentUrl:
            "https://firebasestorage.googleapis.com/v0/b/thtgroup-95584.appspot.com/o/ishikawa_pv.mp4?alt=media&token=6ba0193a-df45-4145-8342-bcc617a336bd",
        },
      },
      introFirstParagraph:
        "石川県金沢市鳴和に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒・トランポリンの４種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekdaySentence: "金沢市の 土日だけ体操教室〈体幹・体づくり教室〉 は、",
      weekendSentence: "金沢市城北町の 土日だけ体操教室 は、少人数制だから",
      newsItems: [],
    },
    coursesFeesDescription:
      "石川県金沢市鳴和の『土日だけ体操教室』土日開催の本格クラスと料金一覧。マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹を強化。キッズ・ジュニア初級からバク転・選手大会クラスまで少人数制で指導。無料体験レッスン受付中。",
    coursesFeesTaikanDescription:
      "石川県金沢市鳴和の土日だけ体操教室〈体幹・体づくり教室〉は、体操式メニューで姿勢・バランス・柔軟性を強化。マット運動・跳び箱・鉄棒・トランポリンを用いた少人数制レッスンで成長フィードバック付き。無料体験受付中！",
  },

  fukui: {
    id: "fukui",
    logo: FukuiLogo,
    weekendPalette: ["#7894f8", "#ff5e5e", "darkorange", "darkgreen"],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle: "福井市の土日だけ体操教室｜幼児・小学生向け｜バク転・体幹強化",
      metaDescription:
        "福井県福井市の『土日だけ体操教室 福井店』は、幼児(3歳)〜小学生向けの少人数制体操教室。マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹、瞬発力を鍛えます。バク転教室も併設し、無料体験レッスン受付中。",
      regionKeywords: ["福井県", "福井市", "福井店", "幼児 体操教室", "小学生 体操教室"],
      ld: {
        name: "土日だけ体操教室 福井店",
        description:
          "福井県福井市の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒・トランポリンで運動能力を育てます。",
        postalCode: "910-0015",
        streetAddress: "二の宮３丁目４２−１２ ジョイライフ",
        addressLocality: "福井市",
        addressRegion: "福井県",
        openingHours: ["Sa 09:00-12:40", "Su 09:00-12:40"],
        priceRange: "7590円/月",
        video: {
          name: "土日だけ体操教室 福井店 教室紹介動画",
          description:
            "福井県福井市の土日だけ体操教室の教室紹介動画です。マット運動・跳び箱・鉄棒・トランポリンで楽しく体操を学べます。",
          thumbnailUrl:
            "https://tht-group.co.jp/thumbnails/fukui_thumbnail.jpg",
          contentUrl:
            "https://firebasestorage.googleapis.com/v0/b/thtgroup-95584.appspot.com/o/fukui_pv.mp4?alt=media&token=6ba0193a-df45-4145-8342-bcc617a336bd",
        },
      },
      introFirstParagraph:
        "福井県福井市に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒・トランポリンの４種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekendSentence: "福井市の 土日だけ体操教室 は、少人数制だから",
      newsItems: [],
    },
    coursesFeesDescription:
      "福井県福井市の『土日だけ体操教室』土日開催の本格クラスと料金一覧。幼児(3歳)から小学生まで、マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹を強化。バク転クラスまで少人数制で指導。無料体験レッスン受付中。",
  },

  mutsukawa: {
    id: "mutsukawa",
    logo: MutsukawaLogo,
    weekendPalette: [
      "#7894f8",
      "#7894f8",
      "#ff5e5e",
      "darkorange",
      "darkgreen",
      "darkgreen",
    ],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle:
        "横浜市南区六ツ川の土日だけ体操教室｜南区六ツ川体操教室・バク転・体幹強化",
      metaDescription:
        "横浜市南区六ツ川の『土日だけ体操教室 南区六ツ川店』は、マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹、瞬発力を鍛える少人数制体操教室。バク転教室やパーソナル教室も併設し、体操式メニューで挑戦と成功をサポート。成長フィードバック付き無料体験レッスン受付中。",
      regionKeywords: ["神奈川県", "横浜市", "南区六ツ川店"],
      ld: {
        name: "土日だけ体操教室 南区六ツ川店",
        description:
          "神奈川県横浜市の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒・トランポリンで運動能力を育てます。",
        postalCode: "232-0066",
        streetAddress: "南区六ツ川４丁目1142-1F 第１笠原ビル",
        addressLocality: "横浜市",
        addressRegion: "神奈川県",
        openingHours: ["Sa 09:00-12:40", "Su 09:00-12:40"],
        priceRange: "7590円/月",
        // 六ツ川は元データに紹介動画の記載なし
      },
      introFirstParagraph:
        "神奈川県横浜市に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒・トランポリンの４種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekendSentence: "神奈川県横浜市の 土日だけ体操教室 は、少人数制だから",
      newsItems: [],
    },
    coursesFeesDescription:
      "横浜市南区六ツ川の『土日だけ体操教室』土日開催の本格クラスと料金一覧。マット運動・跳び箱・鉄棒・トランポリンで柔軟性と体幹を強化。キッズ・ジュニア初級からバク転・選手大会クラスまで少人数制で指導。無料体験レッスン受付中。",
  },

  komatsu: {
    id: "komatsu",
    logo: KomatsuLogo,
    weekendPalette: [
      "#ff5e5e",
      "darkorange",
      "darkgreen",
      "blueviolet",
      "#7894f8",
      "cornflowerblue",
    ],
    weekdayPalette: weekdayPalette5,
    top: {
      metaTitle:
        "【石川/小松市】小松体操教室｜バク転・運動苦手克服｜3歳(幼児)〜小学生向け",
      metaDescription:
        "石川県小松市の子供向け体操教室「小松体操教室」。バク転、跳び箱、逆上がりなど運動が苦手な子も安心！2歳/3歳(幼児)から小学生まで、楽しみながら運動能力・体幹を伸ばします。まずは無料体験へ！",
      regionKeywords: ["石川県", "小松市", "小松体操教室"],
      ld: {
        name: "小松体操教室",
        description:
          "石川県小松市の土日専門子供向け体操教室。マット運動・跳び箱・鉄棒でバク転や運動能力を育てます。",
        postalCode: "923-0804",
        streetAddress: "光町４７",
        addressLocality: "小松市",
        addressRegion: "石川県",
        openingHours: [],
        priceRange: "6900円/月",
        // 紹介動画は元サイトに記載なし
      },
      introFirstParagraph:
        "石川県小松市に開校した「土日だけ体操教室」は、週末の限られた時間で マット運動・跳び箱・鉄棒の３種目 を思いきり楽しみながら、子どもたちの やる気 と 自信 を育てる体操クラブです。少人数制で安全を第一に、一人ひとりの挑戦を丁寧にサポート。",
      weekendSentence: "小松市の 土日だけ体操教室 は、少人数制だから",
      newsItems: [],
    },
    coursesFeesDescription:
      "石川県小松市の『土日だけ体操教室』土日開催のクラスと料金一覧。マット運動・跳び箱・鉄棒でバク転や運動能力を育てます。キッズからバク転クラスまで少人数制で指導。無料体験レッスン受付中。",
  },
};

// Firestore投入用のシードデータ(scripts/seedBranches.mjsから参照)
export const seedFirestoreBranchDocs: Record<RegionId, FirestoreBranchDoc> = {
  horikawa: {
    roomName: "富山市堀川体操教室",
    zip: "〒939-8072",
    address: "富山県富山市堀川町 355-7 マイプラザ2F（ダイソー前）",
    hasParking: true,
    freeTrialUrl: "https://free-trial.tht-group.co.jp/",
    membershipUrl: "https://membership-entry.tht-group.co.jp/horikawa.html",
    loginUrl: "https://xn--tqqw71afoar1y.jp/user_login.html",
    showCoursesFeesTaikan: true,
    showCoursesFees: true,
    weekendCourses: [
      {
        label: "キッズ・ジュニア初級",
        items: [
          {
            number: 1,
            target: "年少～年中",
            weektime: "土/日 9:20～10:20",
            holitime: "土/日 10:30～11:30",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をします。初めて体操を習うお子様やジュニアクラスへの準備段階の教室になります。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "年長～小学2年",
            weektime: "土/日 10:40～12:10",
            holitime: "土 14:20～15:50",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力づくりを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション・バク転",
        items: [
          {
            number: 1,
            target: "小学生～",
            weektime: "土 16:00～17:30",
            holitime: "日 14:20～15:50",
            count1: "週１回",
            price1: "9,790円／月",
            explanation:
              "練習内容：マット・とび箱・鉄棒を利用しながら、体操の基礎運動の指導をし、柔軟や補強による体力づくりや、バク転などの難しい技にも挑戦していきます。",
          },
        ],
      },
      {
        label: "選手大会",
        items: [
          {
            number: 1,
            target: "年中～",
            weektime: "日 16:00～17:30",
            holitime: "",
            count1: "週１回",
            price1: "14,100円／月",
            explanation:
              "より発展的な練習（体力・精神力・集中力・柔軟性）を中心としています。段階を踏み、体操競技の基本・技術を強化していきます。",
          },
        ],
      },
    ],
    weekdayCourses: [
      {
        label: "体操・体づくり運動・かけっこ",
        items: [
          {
            number: 1,
            target: "3歳～年長",
            weektime: "火/水/金 15:30～16:00",
            holitime: "",
            count1: "週１回",
            price1: "3,278円／月",
            explanation:
              "マット運動やリズム運動、バランス遊びなどを通して、運動への興味や「できた！」の成功体験を重ねていきます。ケガをしにくい身体づくりや、集団行動の基礎も自然と身につけることができます。",
          },
        ],
      },
      {
        label: "体操・体づくり運動・かけっこ",
        items: [
          {
            number: 1,
            target: "年中～小学2年",
            weektime: "火/水/金 16:10～16:50",
            holitime: "火/水/金 17:00～17:40",
            count1: "週１回",
            price1: "3,278円／月",
            explanation:
              "マット・跳び箱・鉄棒などの体操種目にチャレンジしながら、柔軟性・バランス・筋力などの基礎体力を養います。楽しく運動する中で「できた！」を増やし、自信につなげます。",
          },
        ],
      },
      {
        label: "体操・体幹トレーニング・陸上",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "火/水/金 17:50～18:30",
            holitime: "火/水/金 18:40～19:20",
            count1: "週１回",
            price1: "3,278円／月",
            explanation:
              "体操に加え、バランスボールやプランクなどの体幹トレーニングも導入。楽しく動きながら、姿勢改善・ケガ予防・集中力アップに効果的な身体の使い方を身につけます。",
          },
        ],
      },
      {
        label: "【本格的】体幹トレーニング",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "火/水/金 19:30～20:10",
            holitime: "",
            count1: "週１回",
            price1: "3,278円／月",
            explanation:
              "スポーツを本格的にがんばる子どもたちのために、競技力を高める体幹トレーニングを集中的に実施します。正しいフォームや身体の連動性を意識しながら、ブレない軸を育てます。",
          },
        ],
      },
      {
        label: "【本格的】体幹トレーニング",
        items: [
          {
            number: 1,
            target: "中学生・高校生・大人",
            weektime: "火/水/金 20:20～21:00",
            holitime: "",
            count1: "週１回",
            price1: "3,278円／月",
            explanation:
              "スポーツパフォーマンス向上はもちろん、姿勢改善や腰痛予防、身体の安定性向上にも効果的。年齢や目的に合わせたトレーニングで、無理なく理想の身体づくりを目指します。",
          },
        ],
      },
    ],
  },

  takaoka: {
    roomName: "富山県高岡体操教室",
    zip: "〒933-0042",
    address: "富山県高岡市中川栄町６−１０",
    hasParking: true,
    freeTrialUrl: "https://nuts-member.web.app/free_trial/?club=takaoka",
    membershipUrl:
      "https://nuts-memberships-form.web.app/contact.html?club=takaoka",
    loginUrl: "https://nuts-union.web.app/",
    showCoursesFeesTaikan: false,
    showCoursesFees: true,
    weekendCourses: [
      {
        label: "幼児①",
        items: [
          {
            number: 1,
            target: "幼児（3歳～5歳）",
            weektime: "土/日 9:00～9:40",
            holitime: "",
            count1: "週１回",
            price1: "5,478円／月",
            explanation:
              "保護者様と一緒にレッスンを行います。幼児②クラスの準備段階のクラスになります。",
          },
        ],
      },
      {
        label: "幼児②",
        items: [
          {
            number: 1,
            target: "幼児（3歳～5歳）",
            weektime: "土/日 9:50～10:30",
            holitime: "",
            count1: "週１回",
            price1: "5,478円／月",
            explanation:
              "学校体育で行う基礎運動の指導をします。幼児①クラスの内容を習得しているお子様が対象のクラスになります。",
          },
        ],
      },
      {
        label: "キッズ",
        items: [
          {
            number: 1,
            target: "5歳～小学2年",
            weektime: "土/日 10:40～11:40",
            holitime: "",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をします。初めて体操を習うお子様やジュニアクラスへの準備段階の教室になります。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 11:50～13:20",
            holitime: "土/日 14:20～15:50",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力づくりを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション・バク転",
        items: [
          {
            number: 1,
            target: "小学生～",
            weektime: "土/日 16:00～17:30",
            holitime: "",
            count1: "週１回",
            price1: "9,790円／月",
            explanation:
              "練習内容：マット・とび箱・鉄棒を利用しながら、体操の基礎運動の指導をし、柔軟や補強による体力づくりや、バク転などの難しい技にも挑戦していきます。",
          },
        ],
      },
    ],
    weekdayCourses: [],
  },

  tonami: {
    roomName: "富山県砺波体操教室",
    zip: "〒939-1328",
    address: "富山県砺波市大辻７１４",
    hasParking: true,
    freeTrialUrl: "https://nuts-member.web.app/free_trial/?club=tonami",
    membershipUrl:
      "https://nuts-memberships-form.web.app/contact.html?club=tonami",
    loginUrl: "https://nuts-union.web.app/",
    showCoursesFeesTaikan: false,
    showCoursesFees: true,
    weekendCourses: [
      {
        label: "幼児①",
        items: [
          {
            number: 1,
            target: "幼児（3歳～5歳）",
            weektime: "土/日 9:00～9:40",
            holitime: "",
            count1: "週１回",
            price1: "5,478円／月",
            explanation:
              "保護者様と一緒にレッスンを行います。幼児②クラスの準備段階のクラスになります。",
          },
        ],
      },
      {
        label: "幼児②",
        items: [
          {
            number: 1,
            target: "幼児（3歳～5歳）",
            weektime: "土/日 9:50～10:30",
            holitime: "",
            count1: "週１回",
            price1: "5,478円／月",
            explanation:
              "学校体育で行う基礎運動の指導をします。幼児①クラスの内容を習得しているお子様が対象のクラスになります。",
          },
        ],
      },
      {
        label: "キッズ",
        items: [
          {
            number: 1,
            target: "5歳～小学2年",
            weektime: "土/日 10:40～11:40",
            holitime: "",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をします。初めて体操を習うお子様やジュニアクラスへの準備段階の教室になります。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 11:50～13:20",
            holitime: "土/日 14:20～15:50",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力づくりを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション・バク転",
        items: [
          {
            number: 1,
            target: "小学生～",
            weektime: "土/日 16:00～17:30",
            holitime: "",
            count1: "週１回",
            price1: "9,790円／月",
            explanation:
              "練習内容：マット・とび箱・鉄棒を利用しながら、体操の基礎運動の指導をし、柔軟や補強による体力づくりや、バク転などの難しい技にも挑戦していきます。",
          },
        ],
      },
    ],
    weekdayCourses: [],
  },

  ishikawa: {
    roomName: "金沢市城北体操教室",
    zip: "〒920-0804",
    address: "石川県金沢市鳴和２丁目１０−４４",
    hasParking: true,
    freeTrialUrl: "https://nuts-member.web.app/free_trial/?club=ishikawa",
    membershipUrl:
      "https://nuts-memberships-form.web.app/contact.html?club=ishikawa",
    loginUrl: "https://nuts-union.web.app/",
    showCoursesFeesTaikan: true,
    showCoursesFees: false,
    weekendCourses: [
      {
        label: "キッズ・ジュニア初級",
        items: [
          {
            number: 1,
            target: "年少～年中",
            weektime: "土/日 9:20～10:20",
            holitime: "土/日 10:30～11:30",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をします。初めて体操を習うお子様やジュニアクラスへの準備段階の教室になります。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "年長～小学2年",
            weektime: "土/日 10:40～12:10",
            holitime: "土 14:20～15:50",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力づくりを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション・バク転",
        items: [
          {
            number: 1,
            target: "小学生～",
            weektime: "土 16:00～17:30",
            holitime: "日 14:20～15:50",
            count1: "週１回",
            price1: "9,790円／月",
            explanation:
              "練習内容：マット・とび箱・鉄棒を利用しながら、体操の基礎運動の指導をし、柔軟や補強による体力づくりや、バク転などの難しい技にも挑戦していきます。",
          },
        ],
      },
    ],
    weekdayCourses: [
      {
        label: "体操・トランポリン・体づくり運動・かけっこ",
        items: [
          {
            number: 1,
            target: "3歳～5歳",
            weektime: "月 城北・鳴和 15:20～15:50",
            holitime: "金 工大前 16:30～17:00",
            count1: "週１回",
            price1: "4,378円／月",
            explanation:
              "マット運動やリズム運動、バランス遊びなどを通して、運動への興味や「できた！」の成功体験を重ねていきます。ケガをしにくい身体づくりや、集団行動の基礎も自然と身につけることができます。",
          },
        ],
      },
      {
        label: "体操・トランポリン・体づくり運動・かけっこ",
        items: [
          {
            number: 1,
            target: "5歳～小学年",
            weektime: "月 城北・鳴和 15:50～16:30",
            holitime: "金 工大前 17:10～17:50 18:00～18:40",
            count1: "週１回",
            price1: "4,378円／月",
            explanation:
              "マット・跳び箱・鉄棒などの体操種目にチャレンジしながら、柔軟性・バランス・筋力などの基礎体力を養います。楽しく運動する中で「できた！」を増やし、自信につなげます。",
          },
        ],
      },
      {
        label: "体操・トランポリン・体幹トレーニング・陸上",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "月 城北・鳴和 16:30～17:15 17:15～18:00",
            holitime: "金 工大前 17:50～18:30 19:45～20:30",
            count1: "週１回",
            price1: "4,378円／月",
            explanation:
              "体操に加え、バランスボールやプランクなどの体幹トレーニングも導入。楽しく動きながら、姿勢改善・ケガ予防・集中力アップに効果的な身体の使い方を身につけます。",
          },
        ],
      },
      {
        label: "体操・バク転",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "月曜 18:00～18:45",
            holitime: "",
            count1: "週１回",
            price1: "4,378円／月",
            explanation:
              "スポーツを本格的にがんばる子どもたちのために、競技力を高める体幹トレーニングを集中的に実施します。正しいフォームや身体の連動性を意識しながら、ブレない軸を育てます。",
          },
        ],
      },
      {
        label: "【本格的】体幹トレーニング",
        items: [
          {
            number: 1,
            target: "中学生・高校生・大人",
            weektime: "火/水/金 20:20～21:00",
            holitime: "",
            count1: "週１回",
            price1: "4,378円／月",
            explanation:
              "スポーツパフォーマンス向上はもちろん、姿勢改善や腰痛予防、身体の安定性向上にも効果的。年齢や目的に合わせたトレーニングで、無理なく理想の身体づくりを目指します。",
          },
        ],
      },
    ],
  },

  fukui: {
    roomName: "福井体操教室",
    zip: "〒910-0015",
    address: "福井県福井市二の宮３丁目４２−１２ ジョイライフ",
    hasParking: true,
    freeTrialUrl: "https://nuts-member.web.app/free_trial/?club=fukui",
    membershipUrl:
      "https://nuts-memberships-form.web.app/contact.html?club=fukui",
    loginUrl: "https://nuts-union.web.app/",
    showCoursesFeesTaikan: false,
    showCoursesFees: true,
    weekendCourses: [
      {
        label: "幼児",
        items: [
          {
            number: 1,
            target: "幼児（3歳～5歳）",
            weektime: "土/日 9:00～9:40",
            holitime: "日 16:40～17:30",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "保護者様と一緒にレッスンを行います。幼児②クラスの準備段階のクラスになります。",
          },
        ],
      },
      {
        label: "キッズ",
        items: [
          {
            number: 1,
            target: "5歳～小学2年",
            weektime: "土/日 10:50～11:40",
            holitime: "日 14:50～15:40",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をします。初めて体操を習うお子様やジュニアクラスへの準備段階の教室になります。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 10:50～11:40",
            holitime: "日 13:50～14:40",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力づくりを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション・バク転",
        items: [
          {
            number: 1,
            target: "小学生～",
            weektime: "土/日 11:50～12:40",
            holitime: "日 16:40～17:30",
            count1: "週１回",
            price1: "7,590円／月",
            explanation:
              "練習内容：マット・とび箱・鉄棒を利用しながら、体操の基礎運動の指導をし、柔軟や補強による体力づくりや、バク転などの難しい技にも挑戦していきます。",
          },
        ],
      },
    ],
    weekdayCourses: [],
  },

  mutsukawa: {
    roomName: "南区六ツ川体操教室",
    zip: "〒232-0066",
    address: "神奈川県横浜市南区六ツ川４丁目1142-1F 第１笠原ビル",
    hasParking: true,
    freeTrialUrl: "https://nuts-member.web.app/free_trial/?club=yokohama",
    membershipUrl:
      "https://nuts-memberships-form.web.app/contact.html?club=yokohama",
    loginUrl: "https://nuts-union.web.app/",
    showCoursesFeesTaikan: false,
    showCoursesFees: true,
    weekendCourses: [
      {
        label: "リトル",
        items: [
          {
            number: 1,
            target: "3歳～年長",
            weektime: "土/日 9:10～10:00(50分)",
            holitime: "",
            count1: "週１回",
            price1: "7,900円(税抜)／8,690円(税込)",
            explanation:
              "少しずつ自分の力でチャレンジしながら、マット・とび箱・鉄棒・トランポリンを使った基本運動を通して、体を動かす楽しさを育てます。",
          },
        ],
      },
      {
        label: "キッズ",
        items: [
          {
            number: 1,
            target: "年長～小学2年",
            weektime: "土/日 10:10～11:10(60分)",
            holitime: "",
            count1: "週１回",
            price1: "7,900円(税抜)／8,690円(税込)",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をします。初めて体操を習うお子様やジュニアクラスへの準備段階の教室になります。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 11:20～12:40(80分)",
            holitime: "",
            count1: "週１回",
            price1: "7,900円(税抜)／8,690円(税込)",
            explanation:
              "マット・とび箱・鉄棒・トランポリンを利用しながら、丈夫な体づくりを目的に、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力づくりを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 13:50～15:10(80分)",
            holitime: "",
            count1: "週１回",
            price1: "9,900円(税抜)／10,890円(税込)",
            explanation:
              "練習内容：マット・とび箱・鉄棒を利用しながら、体操の基礎運動の指導をし、柔軟や補強による体力づくりや、バク転などの難しい技にも挑戦していきます。",
          },
        ],
      },
      {
        label: "体操体幹リトル",
        items: [
          {
            number: 1,
            target: "3歳～年長",
            weektime: "土/日 15:20～15:50(30分)",
            holitime: "",
            count1: "週１回",
            price1: "4,980円(税抜)／5,478円(税込)",
            explanation:
              "マット運動やリズム運動、バランス遊びなどを通して、運動への興味や「できた！」の成功体験を重ねていきます。ケガをしにくい身体づくりや、集団行動の基礎も自然と身につけることができます。",
          },
        ],
      },
      {
        label: "体操体幹キッズ",
        items: [
          {
            number: 1,
            target: "年長～小学2年",
            weektime: "土/日 16:00～16:40(40分)",
            holitime: "",
            count1: "週１回",
            price1: "4,980円(税抜)／5,478円(税込)",
            explanation:
              "マット運動やリズム運動、バランス遊びなどを通して、運動への興味や「できた！」の成功体験を重ねていきます。ケガをしにくい身体づくりや、集団行動の基礎も自然と身につけることができます。",
          },
        ],
      },
      {
        label: "体操体幹ジュニア",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 16:50～17:35(45分)",
            holitime: "",
            count1: "週１回",
            price1: "4,980円(税抜)／5,478円(税込)",
            explanation:
              "マット・跳び箱・鉄棒などの体操種目にチャレンジしながら、柔軟性・バランス・筋力などの基礎体力を養います。楽しく運動する中で「できた！」を増やし、自信につなげます。",
          },
        ],
      },
    ],
    weekdayCourses: [],
  },

  komatsu: {
    roomName: "小松体操教室",
    zip: "〒923-0804",
    address: "石川県小松市光町４７",
    hasParking: false,
    freeTrialUrl: "https://nuts-member.web.app/free_trial/?club=komatsu",
    membershipUrl:
      "https://nuts-memberships-form.web.app/contact.html?club=komatsu",
    loginUrl: "https://nuts-union.web.app/",
    showCoursesFeesTaikan: false,
    showCoursesFees: true,
    weekendCourses: [
      {
        label: "キッズ",
        items: [
          {
            number: 1,
            target: "幼児5歳〜小学2年生",
            weektime: "土/日 ※1回60分",
            holitime: "",
            count1: "週１回",
            price1: "6,900円(税抜)／月",
            explanation:
              "マット・とび箱・鉄棒を利用しながら、丈夫な体作りを目的に、楽しく元気いっぱいに練習を行い、集団の中でのルールを身につけます。",
          },
        ],
      },
      {
        label: "ジュニア",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 ※1回90分",
            holitime: "",
            count1: "週１回",
            price1: "6,900円(税抜)／月",
            explanation:
              "マット・とび箱・鉄棒を利用しながら、学校体育でも行う基礎運動の指導をし、柔軟や補強による体力作りを中心とした練習をしていきます。",
          },
        ],
      },
      {
        label: "クッション・バク転",
        items: [
          {
            number: 1,
            target: "小学生～",
            weektime: "土/日 ※1回90分",
            holitime: "",
            count1: "週１回",
            price1: "8,900円(税抜)／月",
            explanation:
              "体操の基礎運動の指導をし、柔軟や補強による体力作りや、バク転や宙返りなどの難しい技などにも挑戦していきます。",
          },
        ],
      },
      {
        label: "キッズバク転",
        items: [
          {
            number: 1,
            target: "幼児5歳〜小学2年生",
            weektime: "土/日 ※1回60分",
            holitime: "",
            count1: "週１回",
            price1: "8,900円(税抜)／月",
            explanation:
              "クッションクラスに上がるための準備クラスです。マット・とび箱・鉄棒を利用しながら、丈夫な体作りを目的に、楽しく元気いっぱいに練習を行い、集団の中でのルールを身につけます。",
          },
        ],
      },
      {
        label: "体操・体づくり運動・かけっこ※体幹クラスです",
        items: [
          {
            number: 1,
            target: "幼児5歳〜小学2年生",
            weektime: "土/日 12:45～13:30",
            holitime: "",
            count1: "週１回",
            price1: "5,478円／月",
            explanation:
              "マット運動やリズム運動、バランス遊びなどを通して、運動への興味や「できた！」の成功体験を重ねていきます。ケガをしにくい身体づくりや、集団行動の基礎も自然と身につけることができます。",
          },
        ],
      },
      {
        label: "体操・体づくり運動・かけっこ※体幹クラスです",
        items: [
          {
            number: 1,
            target: "小学生",
            weektime: "土/日 13:30～14:15",
            holitime: "",
            count1: "週１回",
            price1: "5,478円／月",
            explanation:
              "マット・跳び箱・鉄棒などの体操種目にチャレンジしながら、柔軟性・バランス・筋力などの基礎体力を養います。楽しく運動する中で「できた！」を増やし、自信につなげます。",
          },
        ],
      },
    ],
    weekdayCourses: [],
  },
};
